import { type SyntheticEvent } from "react";
import { useNavigate, useLocation, Routes, Route, Navigate } from "react-router-dom";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import BasicInfo from "containers/admin/clients/client/components/basic-info/basic-info";
import EmailTypes from "containers/admin/clients/client/components/email-types/email-types";
import { Typography } from "ui/typography";
import { Tab, Tabs } from "ui/tabs";
import toast from "components/partials/toast/toast";

import { ClientWriteBody, useCurrentClient, useReplaceClient } from "hooks/api/use-clients";

import { getErrorMessage } from "utilities";

import { usePagePermission } from "hooks/use-page-permission";

import { Permission } from "types/auth";
import PageContent from "components/partials/page-content/page-content";

const EDIT_TABS = ["Basic information", "Email types"];

const EDIT_TABS_SX: SxProps<Theme> = { mb: 4 };

export const ProfileEditPage = () => {
  const client = useCurrentClient();

  const replaceClientMutation = useReplaceClient();
  const navigate = useNavigate();
  const location = useLocation();

  const clientProfileRoute = `/clients/${client.id}/profile`;

  usePagePermission(Permission.PERM_CLIENT_WRITE);

  const updateClient = (attributes: ClientWriteBody & { id: string }) => {
    const { id, ...data } = attributes;
    replaceClientMutation
      .mutateAsync({ id, data })
      .then(() => {
        toast.success({
          data: {
            title: "Updated client",
          },
        });
        navigate(clientProfileRoute);
      })
      .catch((err) => {
        toast.error({
          data: {
            title: "Failed to save client",
            message: getErrorMessage(err?.response?.data),
          },
        });
      });
  };

  const cancelEditClient = () => {
    navigate(clientProfileRoute);
  };

  const handleTabChange = (_event: SyntheticEvent, option: string) => {
    const basePath = `${clientProfileRoute}/edit`;
    navigate(option === "Basic information" ? `${basePath}/basic` : `${basePath}/email-types`, {
      replace: true,
    });
  };

  const currentPage = () => {
    return location.pathname.endsWith("basic") ? "Basic information" : "Email types";
  };

  return (
    <PageContent>
      <Typography variant="h2" sx={{ color: "secondary.main" }} className="mb-8 pt-5">
        Update Client Profile
      </Typography>
      <Box>
        <Tabs
          aria-label="edit-type-toggle"
          value={currentPage()}
          onChange={handleTabChange}
          sx={EDIT_TABS_SX}>
          {EDIT_TABS.map((name) => (
            <Tab key={name} value={name} label={name} />
          ))}
        </Tabs>
      </Box>
      <Routes>
        <Route path="/" element={<Navigate to="basic" replace />} />
        <Route
          path="basic"
          element={
            <BasicInfo client={client} onSubmit={updateClient} onCancel={cancelEditClient} />
          }
        />
        <Route path="email-types" element={<EmailTypes client={client} />} />
      </Routes>
    </PageContent>
  );
};
